<script setup lang="ts">
import type { CommissioningPollingPhase } from '../composables/useCommissioningPollingTimer'
import { SolAlert, SolButton } from '@solfacil/girassol'
import { computed, onUnmounted, ref, watch } from 'vue'

interface Props {
  phase: CommissioningPollingPhase
}

const props = defineProps<Props>()

const router = useRouter()

const PROGRESS_STEP_COUNT = 4
const PROGRESS_CYCLE_MS = 1_200

const activeProgressIndex = ref(0)
let progressIntervalId: ReturnType<typeof setInterval> | null = null

const HEADINGS: Record<CommissioningPollingPhase, string> = {
  ANALYZING: 'Analisando dados…',
  ALMOST: 'Só mais alguns instantes…',
  EXTENDED: 'Nós te avisaremos assim que a análise for concluída',
}

const heading = computed(() => HEADINGS[props.phase])

const description = computed(() => {
  if (props.phase === 'EXTENDED') {
    return 'A validação da instalação, em geral, é automática e concluída em minutos mas, em alguns casos, pode ser necessária uma análise manual da nossa equipe.'
  }

  return 'Estamos verificando as informações enviadas. Aguarde um momento'
})

function stopProgressCycle() {
  if (progressIntervalId !== null) {
    clearInterval(progressIntervalId)
    progressIntervalId = null
  }
}

function startProgressCycle() {
  stopProgressCycle()
  progressIntervalId = setInterval(() => {
    activeProgressIndex.value = (activeProgressIndex.value + 1) % PROGRESS_STEP_COUNT
  }, PROGRESS_CYCLE_MS)
}

watch(() => props.phase, (phase, previousPhase) => {
  if (phase === 'EXTENDED') {
    stopProgressCycle()
    return
  }

  if (previousPhase === 'EXTENDED' || previousPhase === undefined)
    activeProgressIndex.value = 0

  startProgressCycle()
}, { immediate: true })

onUnmounted(stopProgressCycle)

const showProgressTrack = computed(() => props.phase !== 'EXTENDED')

function goToProjects() {
  router.push('/projects')
}
</script>

<template>
  <div class="commissioning-polling box-border w-full min-w-0 px-2xs py-2xs rounded-lg bg-neutral-high-pure">
    <div class="commissioning-polling__content flex flex-col justify-center items-center box-border w-full min-w-0 min-h-[40vh] gap-4 py-xs text-center">
      <div class="spinner-container" />

      <div class="flex flex-col gap-2 max-w-[520px]">
        <h2 class="font-highlight text-xs text-neutral-low-dark">
          {{ heading }}
        </h2>

        <p class="text-2xs text-neutral-low-light">
          {{ description }}
        </p>
      </div>

      <SolAlert
        v-if="phase === 'EXTENDED'"
        id="commissioning-extended-info"
        feedback="informative"
        class="commissioning-polling__alert w-full max-w-[520px] text-left"
      >
        <div>
          <span class="font-bold">Não se preocupe, basta aguardar e nós te avisaremos por e-mail.</span>
          {{ ' ' }}
          <span>O prazo máximo desta análise é de 2 dias úteis.</span>
        </div>
      </SolAlert>

      <ol
        v-if="showProgressTrack"
        class="commissioning-polling__progress flex items-center justify-center gap-3 list-none p-0 m-0"
        aria-label="Progresso da verificação"
      >
        <li
          v-for="(_, stepIndex) in 4"
          :key="stepIndex"
          class="commissioning-polling__progress-step"
          :class="{ 'commissioning-polling__progress-step--active': activeProgressIndex === stepIndex }"
          :aria-current="activeProgressIndex === stepIndex ? 'step' : undefined"
        >
          <span class="commissioning-polling__progress-dot" />
        </li>
      </ol>

      <SolButton
        v-if="phase === 'EXTENDED'"
        id="commissioning-back-to-projects"
        variant="secondary"
        class="commissioning-polling__back-btn"
        @click="goToProjects"
      >
        Voltar para meus projetos
      </SolButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.commissioning-polling {
  &__alert {
    @apply mt-2xs;
  }

  &__back-btn {
    @apply w-full max-w-[520px] mt-2xs;

    @screen md:site {
      @apply w-auto;
    }
  }

  &__progress-step {
    @apply flex items-center justify-center w-10 h-10 rounded-full bg-neutral-high-light;
    transition: background-color 0.25s ease;

    &--active {
      @apply bg-brand-secondary-pure;

      .commissioning-polling__progress-dot {
        @apply bg-neutral-high-pure opacity-100;
      }
    }
  }

  &__progress-dot {
    @apply block w-2.5 h-2.5 rounded-full bg-brand-secondary-pure;
    opacity: 0.35;
    transition: opacity 0.25s ease, background-color 0.25s ease;
  }
}

.spinner-container {
  position: relative;
  background-image: url('/images/installation/spinner-inner.png');
  background-size: 50%;
  background-position: center;
  background-repeat: no-repeat;
  width: 100px;
  height: 100px;

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 3px;
    left: 0;
    background-image: url('/images/installation/spinner-outer.png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    animation: commissioning-polling-spin 1s linear infinite;
    transform-origin: center;
  }
}
</style>

<style>
@keyframes commissioning-polling-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
